import { ChevronLeft, ChevronRight, RotateCcw, Tag, Trash2, X, ZoomIn, ZoomOut } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { FotoItem } from '@features/midia/hooks/useFotosObra';
import { TagCatalogo } from '@features/midia/services/fotoTagsService';

import { FotoTagsPopover } from './FotoTagsPopover';

// Interface genérica para foto ou vídeo
export interface MediaItem {
  id: string;
  url: string;
  nome: string;
  tipo: 'foto' | 'video' | 'anexo';
  criado_em?: string;
  origem?: string;
  origem_id?: string;
}

interface PhotoViewerProps {
  // Aceita FotoItem[] (Midia) ou MediaItem[] (Diario)
  fotos?: FotoItem[];
  items?: MediaItem[];
  fotoAtualIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onGoTo?: (index: number) => void;
  modoEscuro?: boolean;
  onDelete?: (item: FotoItem | MediaItem) => Promise<boolean>;
  // Tags
  tagsMap?: Map<string, TagCatalogo[]>;
  obraId?: string;
  usuarioId?: string;
  onTagsChanged?: () => void;
}

export default function PhotoViewer({
  fotos,
  items,
  fotoAtualIndex,
  isOpen,
  onClose,
  onPrevious,
  onNext,
  onGoTo,
  modoEscuro = false,
  onDelete,
  tagsMap,
  obraId,
  usuarioId,
  onTagsChanged
}: PhotoViewerProps) {
  // Normaliza para uma lista genérica
  const mediaList: MediaItem[] = items
    ? items
    : (fotos || []).map(f => ({ ...f, tipo: 'foto' as const }));

  const itemAtual = mediaList[fotoAtualIndex];
  const isFoto = itemAtual?.tipo === 'foto';

  // Tags
  const [tagsPopoverOpen, setTagsPopoverOpen] = useState(false);
  const tagsDoItem = tagsMap?.get(itemAtual?.id) || [];

  // Estados para zoom da galeria
  const [zoomLevel, setZoomLevel] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // --- Zoom/Pan Mobile (pinça e arrastar)
  const MIN_ZOOM = 0.5;
  const MAX_ZOOM = 4;
  const pinchStartDistanceRef = useRef<number | null>(null);
  const pinchStartZoomRef = useRef(1);
  const lastTapTsRef = useRef(0);
  const touchDragStartRef = useRef({ x: 0, y: 0 });

  function clamp(n: number, min: number, max: number) {
    return Math.max(min, Math.min(max, n));
  }

  function distance(t1: React.Touch, t2: React.Touch) {
    const dx = t2.clientX - t1.clientX;
    const dy = t2.clientY - t1.clientY;
    return Math.hypot(dx, dy);
  }

  // Funções para controle de zoom
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, MAX_ZOOM));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => {
      const newZoom = Math.max(prev - 0.5, MIN_ZOOM);
      if (newZoom === 1) {
        setImagePosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleWheelZoom = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.2 : 0.2;
    setZoomLevel(prev => {
      const newZoom = clamp(prev + delta, MIN_ZOOM, MAX_ZOOM);
      if (newZoom === 1) {
        setImagePosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  };

  // Funções para arrastar a imagem quando com zoom
  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoomLevel > 1) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // --- Touch: pinça (duas pontas) e pan (um dedo)
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length === 2) {
      const d = distance(e.touches[0], e.touches[1]);
      pinchStartDistanceRef.current = d;
      pinchStartZoomRef.current = zoomLevel;
    } else if (e.touches.length === 1) {
      const now = Date.now();
      if (now - lastTapTsRef.current < 300) {
        setZoomLevel(prev => {
          const next = prev === 1 ? 2 : 1;
          if (next === 1) setImagePosition({ x: 0, y: 0 });
          return next;
        });
        lastTapTsRef.current = 0;
      } else {
        lastTapTsRef.current = now;
      }
      if (zoomLevel > 1) {
        setIsDragging(true);
        const t = e.touches[0];
        touchDragStartRef.current = { x: t.clientX - imagePosition.x, y: t.clientY - imagePosition.y };
      }
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length === 2 && pinchStartDistanceRef.current) {
      const d = distance(e.touches[0], e.touches[1]);
      const scale = d / pinchStartDistanceRef.current;
      const nextZoom = clamp(pinchStartZoomRef.current * scale, MIN_ZOOM, MAX_ZOOM);
      setZoomLevel(nextZoom);
      if (nextZoom === 1) setImagePosition({ x: 0, y: 0 });
    } else if (e.touches.length === 1 && isDragging && zoomLevel > 1) {
      const t = e.touches[0];
      setImagePosition({
        x: t.clientX - touchDragStartRef.current.x,
        y: t.clientY - touchDragStartRef.current.y,
      });
    }
  };

  const handleTouchEnd = (_e: React.TouchEvent<HTMLDivElement>) => {
    if (pinchStartDistanceRef.current && zoomLevel === 1) {
      setImagePosition({ x: 0, y: 0 });
    }
    pinchStartDistanceRef.current = null;
    setIsDragging(false);
  };

  // Navegação por teclado
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!isOpen || tagsPopoverOpen) return;

    switch (event.key) {
      case 'Escape':
        onClose();
        break;
      case 'ArrowLeft':
        if (fotoAtualIndex > 0) onPrevious();
        break;
      case 'ArrowRight':
        if (fotoAtualIndex < mediaList.length - 1) onNext();
        break;
    }
  }, [isOpen, tagsPopoverOpen, fotoAtualIndex, mediaList.length, onClose, onPrevious, onNext]);

  // Reset zoom ao abrir modal ou mudar foto
  useEffect(() => {
    if (isOpen) {
      setZoomLevel(1);
      setImagePosition({ x: 0, y: 0 });
      setTagsPopoverOpen(false);
    }
  }, [isOpen, fotoAtualIndex]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';

      const touchMoveHandler = (e: TouchEvent) => {
        if (e.touches.length === 2 || (e.touches.length === 1 && isDragging && zoomLevel > 1)) {
          e.preventDefault();
        }
      };

      const touchStartHandler = (e: TouchEvent) => {
        if (e.touches.length === 2) {
          e.preventDefault();
        }
      };

      document.addEventListener('touchmove', touchMoveHandler, { passive: false });
      document.addEventListener('touchstart', touchStartHandler, { passive: false });

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('touchmove', touchMoveHandler);
        document.removeEventListener('touchstart', touchStartHandler);
        document.body.style.overflow = 'unset';
      };
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown, isDragging, zoomLevel]);

  if (!isOpen || !itemAtual) return null;

  const getOrigemLabel = (origem?: string) => {
    switch (origem) {
      case 'diarios': return 'Diário';
      case 'checklist': return 'Checklist';
      case 'tarefas': return 'Tarefa';
      default: return origem || '';
    }
  };

  const getOrigemColor = (origem?: string) => {
    switch (origem) {
      case 'diarios':
        return modoEscuro ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-100 text-blue-800';
      case 'checklist':
        return modoEscuro ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-800';
      case 'tarefas':
        return modoEscuro ? 'bg-orange-900/20 text-orange-400' : 'bg-orange-100 text-orange-800';
      default:
        return modoEscuro ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700';
    }
  };

  const handleFecharTagsPopover = () => {
    setTagsPopoverOpen(false);
    onTagsChanged?.();
  };

  const handleNavegar = (index: number) => {
    if (onGoTo) {
      onGoTo(index);
    } else {
      const diff = index - fotoAtualIndex;
      if (diff > 0) {
        for (let i = 0; i < diff; i++) onNext();
      } else if (diff < 0) {
        for (let i = 0; i < Math.abs(diff); i++) onPrevious();
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
      {/* Header */}
      <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10">
        <div className="flex items-center gap-3">
          {itemAtual.origem && (
            <span className={`px-2 py-1 rounded-full text-xs font-ageo-regular ${getOrigemColor(itemAtual.origem)}`}>
              {getOrigemLabel(itemAtual.origem)}
            </span>
          )}
          <span className={`text-sm font-ageo-regular ${modoEscuro ? 'text-gray-300' : 'text-white'}`}>
            {fotoAtualIndex + 1} de {mediaList.length}
          </span>
        </div>

        {/* Controles de Zoom (apenas para fotos) */}
        {isFoto && (
          <div className={`flex items-center gap-1 p-2 rounded-lg ${
            modoEscuro ? "bg-gray-800/80" : "bg-white/80"
          }`}>
            <button
              onClick={handleZoomOut}
              className={`p-2 rounded transition-colors ${
                modoEscuro
                  ? "text-gray-100 hover:bg-gray-700"
                  : "text-gray-800 hover:bg-gray-200"
              }`}
              title="Diminuir zoom"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className={`text-xs font-ageo-regular px-2 ${
              modoEscuro ? "text-gray-300" : "text-gray-600"
            }`}>
              {Math.round(zoomLevel * 100)}%
            </span>
            <button
              onClick={handleZoomIn}
              className={`p-2 rounded transition-colors ${
                modoEscuro
                  ? "text-gray-100 hover:bg-gray-700"
                  : "text-gray-800 hover:bg-gray-200"
              }`}
              title="Aumentar zoom"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            {zoomLevel > 1 && (
              <button
                onClick={handleResetZoom}
                className={`p-2 rounded transition-colors ${
                  modoEscuro
                    ? "text-gray-100 hover:bg-gray-700"
                    : "text-gray-800 hover:bg-gray-200"
                }`}
                title="Resetar zoom"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            )}
          </div>
        )}

        <div className="flex items-center gap-2">
          {/* Botão de Tags (apenas para fotos) */}
          {isFoto && obraId && (
            <button
              onClick={() => setTagsPopoverOpen(true)}
              className={`p-2 rounded-full transition-colors relative ${
                modoEscuro
                  ? 'bg-gray-800/80 text-gray-100 hover:bg-gray-800'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              title="Tags da foto"
            >
              <Tag className="w-5 h-5" />
              {tagsDoItem.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center font-ageo-bold">
                  {tagsDoItem.length}
                </span>
              )}
            </button>
          )}
          {onDelete && (
            <button
              onClick={async () => {
                if (confirm('Deseja excluir esta foto?')) {
                  const ok = await onDelete(itemAtual as any);
                  if (ok) onClose();
                }
              }}
              className={`p-2 rounded-full transition-colors ${
                modoEscuro
                  ? 'bg-gray-800/80 text-gray-100 hover:bg-gray-800'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              aria-label="Excluir foto"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={onClose}
            className={`p-2 rounded-full transition-colors ${
              modoEscuro
                ? 'bg-gray-800/80 text-gray-100 hover:bg-gray-800'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
            aria-label="Fechar galeria"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="relative w-full h-full flex items-center justify-center px-4 py-20">
        {/* Botão Anterior */}
        {fotoAtualIndex > 0 && (
          <button
            onClick={onPrevious}
            className={`absolute left-4 p-3 rounded-full transition-colors z-10 ${
              modoEscuro
                ? 'bg-gray-800/80 text-gray-100 hover:bg-gray-800'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
            aria-label="Anterior"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}

        {/* Container da mídia com zoom e arrastar */}
        <div
          className="flex-1 flex items-center justify-center bg-transparent overflow-hidden relative touch-none overscroll-contain"
          onWheel={isFoto ? handleWheelZoom : undefined}
          onMouseDown={isFoto ? handleMouseDown : undefined}
          onMouseMove={isFoto ? handleMouseMove : undefined}
          onMouseUp={isFoto ? handleMouseUp : undefined}
          onMouseLeave={isFoto ? handleMouseUp : undefined}
          onTouchStart={isFoto ? handleTouchStart : undefined}
          onTouchMove={isFoto ? handleTouchMove : undefined}
          onTouchEnd={isFoto ? handleTouchEnd : undefined}
          onTouchCancel={isFoto ? handleTouchEnd : undefined}
          style={{ cursor: isFoto && zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
        >
          {isFoto ? (
            <img
              src={itemAtual.url}
              alt={itemAtual.nome}
              className="object-contain rounded-md select-none"
              style={{
                transform: `scale(${zoomLevel}) translate(${imagePosition.x / zoomLevel}px, ${imagePosition.y / zoomLevel}px)`,
                maxWidth: '90vw',
                maxHeight: '90vh',
                transition: isDragging ? 'none' : 'transform 0.2s ease-out'
              }}
              draggable={false}
              loading="lazy"
            />
          ) : (
            <video
              src={itemAtual.url}
              controls
              className="max-w-[90vw] max-h-[90vh] object-contain rounded-md"
              autoPlay
            />
          )}
        </div>

        {/* Botão Próximo */}
        {fotoAtualIndex < mediaList.length - 1 && (
          <button
            onClick={onNext}
            className={`absolute right-4 p-3 rounded-full transition-colors z-10 ${
              modoEscuro
                ? 'bg-gray-800/80 text-gray-100 hover:bg-gray-800'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
            aria-label="Próximo"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Footer com informações e tags */}
      <div className="absolute bottom-4 left-4 right-4 z-10">
        <div className={`rounded-lg p-4 ${
          modoEscuro
            ? 'bg-gray-800/90 backdrop-blur-sm'
            : 'bg-white/90 backdrop-blur-sm'
        }`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h3 className={`font-ageo-bold text-base mb-1 truncate ${
                modoEscuro ? 'text-gray-100' : 'text-gray-900'
              }`}>
                {itemAtual.nome}
              </h3>
              {itemAtual.criado_em && (
                <p className={`text-sm font-ageo-regular ${
                  modoEscuro ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {new Date(itemAtual.criado_em).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              )}
            </div>
          </div>

          {/* Tags da foto */}
          {isFoto && tagsDoItem.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {tagsDoItem.map(tag => (
                <span
                  key={tag.id}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-ageo-medium ${
                    modoEscuro
                      ? 'bg-blue-900/50 text-blue-300'
                      : 'bg-blue-100 text-blue-700'
                  }`}
                >
                  <Tag className="w-3 h-3" />
                  {tag.nome}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Indicadores de navegação (pontos) - apenas com poucas fotos */}
      {mediaList.length > 1 && mediaList.length <= 20 && (
        <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
          {mediaList.map((_, index) => (
            <button
              key={index}
              onClick={() => handleNavegar(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === fotoAtualIndex ? 'bg-white' : 'bg-white/40'
              }`}
              aria-label={`Ir para item ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Overlay clicável para fechar */}
      <div
        className="absolute inset-0 -z-10"
        onClick={onClose}
        aria-label="Fechar galeria"
      />

      {/* Popover de Tags */}
      {obraId && tagsPopoverOpen && isFoto && (
        <FotoTagsPopover
          isOpen={tagsPopoverOpen}
          onClose={handleFecharTagsPopover}
          fotoId={itemAtual.id}
          fotoUrl={itemAtual.url}
          obraId={obraId}
          usuarioId={usuarioId}
        />
      )}
    </div>
  );
}
